import { Actor, RoleNames } from 'core';
import { isRoles } from 'core/permission/PermissionDSL';
import {
  BeaconReportTableData,
  Metrics,
  ReportDimension,
  ReportRecord
} from 'core/report/ReportData';
import _ from 'lodash';
import { ReportDataProvider } from './ReportDataProvider';
import { tableColumnSettings } from './reportTableColumnSettings';
import { ColumnDefinition } from 'components/TableColumn/TableColumn';

enum COLUMNS {
  NAME = 'name',
  BEACON = 'beacon',
  MESSAGE_COUNT = 'messageCount',
  SPENT = 'spent',
  TAGS = 'tags',
  EDITBTNS = 'editBtns'
}

export class BeaconMessageReportDataProvider implements ReportDataProvider {
  constructor(
    private actor: Actor | null,
    private onDimensionNameClick: (
      dimension: string,
      rowNameValue: string | number
    ) => void,
    private onDateClick: (date: string) => void
  ) {}

  get avalableDimensions() {
    if (
      this.actor?.roleName === RoleNames.beaconAdmin ||
      this.actor?.roleName === RoleNames.beaconExecutive
    ) {
      return [ReportDimension.DAY];
    }
    return [
      ReportDimension.DAY,
      ReportDimension.AGENCY,
      ReportDimension.ADVERTISER,
      ReportDimension.ORDER,
      ReportDimension.L1_OBJECT,
      ReportDimension.L2_OBJECT,
      ReportDimension.CAMPAIGN_TYPE,
      ReportDimension.SALES_CHANNEL,
      ReportDimension.CREATIVE,
      ReportDimension.CREATIVE_SIZE
    ];
  }

  getReportDimensions(actor: Actor | null): ReportDimension[] {
    const isSysAdmin = isRoles(RoleNames.sysAdmin).visible({ actor });
    const isAdvertiser = isRoles(
      RoleNames.adsAdmin,
      RoleNames.adsReport,
      RoleNames.adsSales
    ).visible({ actor });
    const forbidDimensions = isSysAdmin
      ? [ReportDimension.MONTH, ReportDimension.HOUR]
      : [ReportDimension.MONTH, ReportDimension.HOUR, ReportDimension.AGENCY];
    isAdvertiser && forbidDimensions.push(ReportDimension.ADVERTISER);
    return this.avalableDimensions.filter(
      (dimension) => forbidDimensions.indexOf(dimension) === -1
    );
  }

  getReportTableColumnSettings(
    allowMetrics: Metrics[],
    tableData: BeaconReportTableData[],
    dimension: ReportDimension,
    currency: string
  ): ColumnDefinition[] {
    const allColumnSettings = tableColumnSettings(
      tableData,
      dimension,
      currency,
      this.onDimensionNameClick,
      this.onDateClick
    );
    const tableAllowMetrics = [...allowMetrics, COLUMNS.NAME, COLUMNS.EDITBTNS];
    dimension === ReportDimension.CAMPAIGN &&
      tableAllowMetrics.push(COLUMNS.TAGS);
    return _.compact(
      Object.values(COLUMNS).map((column) => {
        return (
          tableAllowMetrics.indexOf(column) !== -1 && allColumnSettings[column]
        );
      })
    );
  }

  getReportTableData(records: ReportRecord[]): BeaconReportTableData[] {
    return records.map((record) => {
      return {
        key: record.dimensionName + _.get(record, 'dimensionID', 0),
        id: _.get(record, 'dimensionID', 0).toString(),
        [COLUMNS.NAME]: record.dimensionName,
        [COLUMNS.BEACON]: record.beacon,
        [COLUMNS.MESSAGE_COUNT]: record.messageCount,
        [COLUMNS.SPENT]: record.spent,
        [COLUMNS.TAGS]: record.tags
      };
    });
  }
}
